import { useCallback, useEffect, useRef, useState } from "react";
import { API } from "../lib/api";
import { useI18n } from "../i18n/LanguageContext";
import { Sparky } from "./Sparky";
import { Send, X, RotateCcw, Bot } from "lucide-react";

/**
 * SparkyChat — bilingual, streaming chat drawer.
 * Opens as a side sheet on desktop, bottom sheet on mobile.
 * Props:
 *   open, onClose
 *   courseId, moduleId
 *   hookPrompt (optional) — seeds a suggested first question
 */
export function SparkyChat({ open, onClose, courseId, moduleId, hookPrompt }) {
  const { t, lang, isRTL } = useI18n();

  const [conversationId, setConversationId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [streaming, setStreaming] = useState(false);
  const [streamText, setStreamText] = useState("");
  const [error, setError] = useState(null);
  const [initError, setInitError] = useState(null);
  const [pendingSend, setPendingSend] = useState(null);

  const scrollRef = useRef(null);
  const abortRef = useRef(null);

  // Establish a new conversation whenever the drawer is opened for a new module/hook/lang
  const startConversation = useCallback(async () => {
    setError(null);
    setInitError(null);
    setMessages([]);
    setStreamText("");
    setConversationId(null);
    try {
      const res = await fetch(`${API}/sparky/conversations`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          course_id: courseId,
          module_id: moduleId,
          language: lang,
          hook_prompt: hookPrompt || null,
        }),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        const reason = data?.detail?.reason || data?.detail || `error_${res.status}`;
        throw new Error(reason);
      }
      const data = await res.json();
      setConversationId(data.conversation_id);
    } catch (e) {
      setInitError(e.message || "start_failed");
    }
  }, [courseId, moduleId, lang, hookPrompt]);

  useEffect(() => {
    if (!open) return;
    startConversation();
    return () => {
      if (abortRef.current) abortRef.current.abort();
    };
  }, [open, startConversation]);

  // Flush any queued message once the conversation is ready
  useEffect(() => {
    if (conversationId && pendingSend && !streaming) {
      const text = pendingSend;
      setPendingSend(null);
      sendMessage(text);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [conversationId, pendingSend]);

  // Autoscroll
  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, streamText]);

  async function sendMessage(text) {
    if (!conversationId || !text.trim() || streaming) return;
    setInput("");
    setError(null);
    setStreamText("");
    const userMsg = { role: "user", content: text.trim() };
    setMessages((m) => [...m, userMsg]);
    setStreaming(true);

    const ac = new AbortController();
    abortRef.current = ac;

    try {
      const res = await fetch(`${API}/sparky/conversations/${conversationId}/messages`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ content: text.trim() }),
        signal: ac.signal,
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        const reason = data?.detail?.reason || data?.detail || `error_${res.status}`;
        if (reason === "llm_unconfigured") setError(t("sparky.warming"));
        else if (reason === "rate_limited") setError(t("sparky.rateLimited"));
        else if (reason === "conversation_limit_reached") setError(t("sparky.convFull"));
        else setError(t("sparky.streamError"));
        setStreaming(false);
        return;
      }

      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";
      let assembled = "";

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const events = buffer.split("\n\n");
        buffer = events.pop() || "";
        for (const evtBlock of events) {
          const lines = evtBlock.split("\n");
          const evLine = lines.find((l) => l.startsWith("event:"));
          const dataLine = lines.find((l) => l.startsWith("data:"));
          if (!evLine || !dataLine) continue;
          const evName = evLine.slice(6).trim();
          const dataStr = dataLine.slice(5).trim();
          let payload;
          try {
            payload = JSON.parse(dataStr);
          } catch {
            continue;
          }
          if (evName === "chunk" && payload.delta) {
            assembled += payload.delta;
            setStreamText(assembled);
          } else if (evName === "done") {
            const finalText = payload.content || assembled;
            setMessages((m) => [...m, { role: "assistant", content: finalText }]);
            setStreamText("");
          } else if (evName === "error") {
            setError(t("sparky.streamError"));
          }
        }
      }
    } catch (e) {
      if (e.name !== "AbortError") setError(t("sparky.streamError"));
    } finally {
      setStreaming(false);
      abortRef.current = null;
    }
  }

  if (!open) return null;

  const suggested = hookPrompt ? [hookPrompt] : [];

  return (
    <div className="fixed inset-0 z-[60]" data-testid="sparky-chat-drawer">
      {/* Backdrop */}
      <div
        onClick={onClose}
        className="absolute inset-0 bg-black/40 backdrop-blur-sm"
        data-testid="sparky-backdrop"
      />

      {/* Panel — side sheet on desktop, bottom sheet on mobile */}
      <div
        className={`absolute bottom-0 left-0 right-0 md:top-0 md:bottom-0 md:${isRTL ? "left-0 md:right-auto" : "right-0 md:left-auto"} md:w-[440px] bg-[#FDFBF7] flex flex-col rounded-t-[2rem] md:rounded-none md:rounded-s-[2rem] shadow-[0_-20px_60px_rgba(0,0,0,0.2)] max-h-[92vh] md:max-h-none md:h-full`}
      >
        {/* Header */}
        <div className="flex items-center gap-3 p-4 border-b-2 border-black/5 bg-white/70">
          <div className={`${streaming ? "animate-bounce" : "animate-float"}`}>
            <Sparky size={52} />
          </div>
          <div className="flex-1 min-w-0">
            <div className="font-black text-[#1A1A1A]" data-testid="sparky-title">{t("sparky.title")}</div>
            <div className="text-xs text-black/60 font-semibold truncate">{t("sparky.subtitle")}</div>
          </div>
          <button
            onClick={startConversation}
            disabled={streaming}
            className="rounded-full h-9 w-9 grid place-items-center border-2 border-black/10 hover:border-black/30 transition disabled:opacity-50"
            title={t("sparky.newChat")}
            data-testid="sparky-reset"
          >
            <RotateCcw className="h-4 w-4" />
          </button>
          <button
            onClick={onClose}
            className="rounded-full h-9 w-9 grid place-items-center border-2 border-black/10 hover:border-black/30 transition"
            title={t("sparky.close")}
            data-testid="sparky-close"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        {/* Messages */}
        <div ref={scrollRef} className="flex-1 overflow-y-auto p-4 space-y-3" data-testid="sparky-messages">
          {initError && (
            <div className="rounded-2xl bg-[#FEE2E2] text-[#991B1B] px-4 py-3 text-sm font-semibold" data-testid="sparky-init-error">
              {initError === "not_enrolled" || initError === "payment_required" || initError === "exam_locked_phase_5"
                ? t("sparky.unauthorized")
                : t("sparky.warming")}
            </div>
          )}

          {!initError && messages.length === 0 && !streamText && (
            <div className="text-sm text-black/60 font-medium" data-testid="sparky-empty">
              {t("sparky.emptyHint")}
              {suggested.length > 0 && (
                <div className="mt-3 space-y-2">
                  {suggested.map((s, i) => (
                    <button
                      key={i}
                      onClick={() => (conversationId ? sendMessage(s) : setPendingSend(s))}
                      disabled={streaming || !!pendingSend}
                      className="w-full text-start rounded-2xl bg-white border-2 border-black/10 hover:border-[#4361EE]/50 px-4 py-3 text-sm font-semibold text-[#1A1A1A] transition disabled:opacity-60"
                      data-testid={`sparky-suggested-${i}`}
                    >
                      {s}
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}

          {messages.map((m, i) => (
            <ChatBubble key={i} msg={m} />
          ))}

          {streaming && (
            <ChatBubble msg={{ role: "assistant", content: streamText || "…" }} streaming />
          )}

          {error && (
            <div className="rounded-2xl bg-[#FEE2E2] text-[#991B1B] px-4 py-3 text-sm font-semibold" data-testid="sparky-error">
              {error}
            </div>
          )}
        </div>

        {/* Input */}
        <form
          onSubmit={(e) => {
            e.preventDefault();
            sendMessage(input);
          }}
          className="p-3 border-t-2 border-black/5 bg-white flex items-center gap-2"
        >
          <input
            value={input}
            onChange={(e) => setInput(e.target.value)}
            disabled={!conversationId || streaming || !!initError}
            placeholder={t("sparky.inputPlaceholder")}
            className="flex-1 rounded-full border-2 border-black/10 focus:border-[#4361EE] outline-none px-4 py-3 text-sm font-medium bg-white disabled:bg-black/5"
            data-testid="sparky-input"
          />
          <button
            type="submit"
            disabled={!input.trim() || !conversationId || streaming || !!initError}
            className="rounded-full h-11 w-11 grid place-items-center bg-[#4361EE] text-white shadow-[0_3px_0_#2A3EB1] hover:translate-y-[1px] hover:shadow-[0_2px_0_#2A3EB1] disabled:opacity-50 disabled:translate-y-0 disabled:shadow-[0_3px_0_#2A3EB1] transition"
            data-testid="sparky-send"
            aria-label={t("sparky.send")}
          >
            <Send className="h-4 w-4" />
          </button>
        </form>
      </div>
    </div>
  );
}

function ChatBubble({ msg, streaming }) {
  const isUser = msg.role === "user";
  return (
    <div className={`flex ${isUser ? "justify-end" : "justify-start"}`} data-testid={`chat-bubble-${msg.role}`}>
      <div
        className={`max-w-[85%] rounded-3xl px-4 py-3 text-sm leading-relaxed whitespace-pre-wrap ${
          isUser
            ? "bg-[#4361EE] text-white rounded-ee-md"
            : "bg-white border-2 border-black/5 text-[#1A1A1A] rounded-es-md shadow-[0_4px_20px_rgba(0,0,0,0.04)]"
        }`}
      >
        {!isUser && (
          <div className="inline-flex items-center gap-1 text-[10px] font-black uppercase tracking-wider text-[#4361EE] mb-1">
            <Bot className="h-3 w-3" /> Sparky
          </div>
        )}
        <div className={!isUser ? "" : "font-medium"}>{msg.content}{streaming && <span className="inline-block w-2 h-4 ms-1 -mb-0.5 bg-current animate-pulse rounded-sm" />}</div>
      </div>
    </div>
  );
}
